import { useCallback, useState } from 'react'
import { useInRouterContext, useLocation } from 'react-router-dom'

// R492 (Dee via Rook #66417): a screen left with something typed keeps it. A form's draft is
// cached per route for the session and restored when the person comes back; the form clears it
// on submit. sessionStorage: one tab, gone when the tab closes, never sent anywhere. Sign-out
// clears every draft (shell-clear.ts), so the next person on a shared device sees none.
// Adopted by: amend (EditItemsDialog) and the new-order form (NewOrderScreen: scopes 'form' and
// 'customer').

export const DRAFT_PREFIX = 'hop.draft:'

export function draftKey(path: string, scope: string): string {
  return `${DRAFT_PREFIX}${path}:${scope}`
}

function store(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

export function readDraft<T>(path: string, scope: string): T | null {
  try {
    const raw = store()?.getItem(draftKey(path, scope))
    return raw == null ? null : (JSON.parse(raw) as T)
  } catch {
    return null
  }
}

export function writeDraft(path: string, scope: string, value: unknown): void {
  try {
    store()?.setItem(draftKey(path, scope), JSON.stringify(value))
  } catch {
    // Storage full or blocked (private mode): the draft lasts only while the screen is open.
  }
}

export function clearDraft(path: string, scope: string): void {
  try {
    store()?.removeItem(draftKey(path, scope))
  } catch {
    /* blocked storage holds nothing to clear */
  }
}

/** Every draft in this tab, and nothing else in its session storage. */
export function clearAllDrafts(): void {
  const s = store()
  if (!s) return
  try {
    const keys: string[] = []
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i)
      if (k && k.startsWith(DRAFT_PREFIX)) keys.push(k)
    }
    keys.forEach((k) => s.removeItem(k))
  } catch {
    /* blocked storage holds nothing to clear */
  }
}

function useRoutePath(): string {
  // Inside the router the route is the router's; a component rendered outside one (a unit
  // test of a dialog) falls back to the document's path. Whether a component sits in a router
  // never changes while it is mounted, so the hook order is stable.
  const inRouter = useInRouterContext()
  return inRouter ? useLocation().pathname : typeof window === 'undefined' ? '/' : window.location.pathname
}

/**
 * The draft for this route and `scope`, read when the component mounts (and again when the route
 * changes under it). `save` stores a new draft, `clear` drops it (call it on submit).
 */
export function useRouteDraft<T>(scope: string): { draft: T | null; save: (value: T) => void; clear: () => void } {
  const path = useRoutePath()
  const [state, setState] = useState<{ path: string; draft: T | null }>(() => ({ path, draft: readDraft<T>(path, scope) }))
  const current = state.path === path ? state : { path, draft: readDraft<T>(path, scope) }
  if (current !== state) setState(current)
  const save = useCallback(
    (value: T) => {
      writeDraft(path, scope, value)
      setState({ path, draft: value })
    },
    [path, scope],
  )
  const clear = useCallback(() => {
    clearDraft(path, scope)
    setState({ path, draft: null })
  }, [path, scope])
  return { draft: current.draft, save, clear }
}
